import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { useQueryClient } from '@tanstack/react-query'

import { supabase, toAppError } from '@/lib/db/supabase'
import { AuthContext } from './auth-context'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null | undefined>(undefined)
  const queryClient = useQueryClient()

  useEffect(() => {
    let active = true

    // Restore the persisted session first so a refresh does not bounce the user
    // to the login screen before the token is read back from storage.
    supabase.auth.getSession().then(({ data }) => {
      if (active) setSession(data.session)
    })

    const { data: subscription } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!active) return
      setSession(nextSession)

      // Wipe cached data on sign-out. Without this, signing in as a different
      // user briefly shows the previous user's figures from the query cache —
      // which in a multi-org product is a data leak, not just a glitch.
      if (event === 'SIGNED_OUT') {
        queryClient.clear()
      }
    })

    return () => {
      active = false
      subscription.subscription.unsubscribe()
    }
  }, [queryClient])

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    })
    if (error) {
      // Supabase returns the same message for a wrong password and an unknown
      // address, which is correct: distinguishing them lets an attacker
      // enumerate who has an account.
      throw new Error(
        error.message === 'Invalid login credentials'
          ? 'Incorrect email or password.'
          : toAppError(error).message,
      )
    }
  }, [])

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw new Error(toAppError(error).message)
    queryClient.clear()
  }, [queryClient])

  const sendPasswordReset = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) throw new Error(toAppError(error).message)
  }, [])

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password })
    if (error) throw new Error(toAppError(error).message)
  }, [])

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      loading: session === undefined,
      signIn,
      signOut,
      sendPasswordReset,
      updatePassword,
    }),
    [session, signIn, signOut, sendPasswordReset, updatePassword],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}
